// A public read-only link (/s/<token>): the shared dashboard under a slim RDX header with the UK
// server clock, a page refresh and a clear "read-only" label. No navigation, filters, widget
// menus, settings or downloads; figures refresh on their own every minute.
import { useEffect } from 'react';
import { useDashboardQuery, useShellQuery } from '../app/api';
import { BrandLogo } from '../ui/BrandLogo';
import { Icon } from '../ui/Icon';
import { ServerClock } from '../ui/ServerClock';
import { formatDateTime, setDisplayTimeZone } from '../lib/format';
import { WidgetFrame } from './WidgetFrame';

export function PublicPage() {
  const { data: shell, isLoading, error } = useShellQuery();
  const dashboardId = shell?.navigation.defaultDashboard ?? '';
  const { data: dashboard } = useDashboardQuery({ id: dashboardId }, { skip: !dashboardId });
  if (shell) setDisplayTimeZone(shell.syncBoundary?.timeZone);
  useEffect(() => {
    if (dashboard && shell) document.title = `${dashboard.title} · ${shell.tenant.name}`;
  }, [dashboard, shell]);

  if (isLoading) return <div className="boot"><div className="state">Loading…</div></div>;
  if (error || !shell) {
    return (
      <div className="login">
        <div className="login-card" style={{ textAlign: 'center', justifyItems: 'center' }}>
          <BrandLogo logo={{ src: '/brand/rdx-logo.svg', alt: 'RDX' }} className="login-logo" />
          <h1>Link not available</h1>
          <div className="muted">This shared view may have been revoked or have expired. Ask whoever sent it for a new link.</div>
        </div>
      </div>
    );
  }
  const poll = shell.realtime?.fallbackPollSeconds ?? 60;
  return (
    <div className="public">
      <header className="topbar public-bar">
        <div className="topbar-row">
          {shell.branding?.logo && <BrandLogo logo={shell.branding.logo} className="public-logo" />}
          <h1 title={dashboard?.title}>{dashboard?.title ?? ''}</h1>
          <div className="topbar-status">
            <ServerClock timeZone={shell.syncBoundary?.timeZone ?? 'UTC'} />
            <span className="badge public-badge" title={`Figures refresh automatically every ${poll} seconds`}>
              <Icon name="eye" size={12} />
              Read-only · refreshes every {poll} s
            </span>
          </div>
          <div className="topbar-end">
            <button className="btn refresh-btn" onClick={() => window.location.reload()} title="Reload the whole page" aria-label="Refresh page">
              <Icon name="reload" size={15} />
              <span className="refresh-label">Refresh</span>
            </button>
          </div>
        </div>
      </header>
      <main className="content">
        {dashboard ? (
          <div className="grid">
            {dashboard.widgets.map((w, i) => (
              <WidgetFrame key={`${w.id}:${dashboard.version}`} index={i} widget={w} dashboardId={dashboard.id} filters={{}} shell={shell} />
            ))}
          </div>
        ) : (
          <div className="skeleton" style={{ height: 320 }} aria-label="Loading" />
        )}
        <footer className="public-foot muted">
          {shell.tenant.name} · shared read-only view{shell.publicView?.expiresAt ? ` · link expires ${formatDateTime(shell.publicView.expiresAt)}` : ''}
        </footer>
      </main>
    </div>
  );
}
